import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Plus, Trash2, Save, Users, Package, Leaf, UserPlus, FileText, MapPin, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminTenantPackages, adminCreateTenantPackage, adminUpdateTenantPackage, adminDeleteTenantPackage,
  adminTenantLeads, adminUpdateTenantLead, adminEditors, createEditor, deleteEditor,
  adminPartners, adminPartnerStatus, adminPartnerDelete,
} from "@/lib/api";

const TID = "kerala";
const BLANK = { name: "", category: "Websites", tagline: "", features: [], price_amount: 0, price_unit: "one-time", price_display: "Starting at", order: 100, active: true, popular: false };
const LEAD_STATUSES = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminKerala() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("packages");
  const [pkgs, setPkgs] = useState(null);
  const [leads, setLeads] = useState(null);
  const [editors, setEditors] = useState(null);
  const [partners, setPartners] = useState(null);
  const [creating, setCreating] = useState(null);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const load = useCallback(() => {
    adminTenantPackages(TID).then((d) => setPkgs(d.packages || [])).catch(() => setPkgs([]));
    adminTenantLeads(TID).then((d) => setLeads(d.leads || [])).catch(() => setLeads([]));
    adminEditors().then((d) => setEditors(d.editors || [])).catch(() => setEditors([]));
    adminPartners(TID).then((d) => setPartners(d.partners || [])).catch(() => setPartners([]));
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const setLeadStatus = async (id, status) => {
    try { await adminUpdateTenantLead(TID, id, { status }); setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l))); }
    catch { toast.error("Update failed"); }
  };

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-emerald-700" size={26} /></div>;

  const TabBtn = ({ id, icon: Icon, children }) => (
    <button onClick={() => setTab(id)} data-testid={`kerala-tab-${id}`}
      className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === id ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
      <Icon size={15} /> {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-kerala">
      <div className="bg-[#024E3F] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <Leaf size={22} className="text-emerald-300" />
          <div>
            <h1 className="font-heading text-2xl font-bold">Kerala.com Admin</h1>
            <p className="text-emerald-100/70 text-sm">Manage the Kerala.com tenant — packages, leads, editors & content (isolated CMS/CRM). Kerala also operates India.</p>
          </div>
          <Link to="/admin/country" className="ml-auto text-xs bg-white/10 border border-white/20 rounded-lg px-3 py-2 hover:bg-white/20" data-testid="kerala-manage-india">Manage India →</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <TabBtn id="packages" icon={Package}>Packages & Pricing</TabBtn>
          <TabBtn id="leads" icon={Users}>Leads {leads ? `(${leads.length})` : ""}</TabBtn>
          <TabBtn id="editors" icon={UserPlus}>Editors {editors ? `(${editors.length})` : ""}</TabBtn>
          <TabBtn id="partners" icon={MapPin}>Partners {partners ? `(${partners.length})` : ""}</TabBtn>
          <Link to="/admin/cms?tenant=kerala" data-testid="kerala-tab-content" className="px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600"><FileText size={15} /> Content (CMS)</Link>
        </div>

        {tab === "packages" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setCreating({ ...BLANK })} data-testid="kerala-add-package" className="inline-flex items-center gap-2 bg-emerald-700 text-white font-semibold rounded-xl px-4 py-2.5 text-sm"><Plus size={16} /> Add package</button>
            </div>
            {pkgs === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-emerald-700" size={24} /></div> : (
              <div className="space-y-3" data-testid="kerala-packages-list">
                {creating && <PackageRow initial={creating} isNew onSaved={() => { setCreating(null); load(); }} onCancel={() => setCreating(null)} />}
                {pkgs.map((p) => <PackageRow key={p.id} initial={p} onSaved={load} />)}
              </div>
            )}
          </div>
        )}

        {tab === "leads" && (
          leads === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-emerald-700" size={24} /></div> :
          leads.length === 0 ? <p className="text-slate-500 text-center py-16">No Kerala leads yet.</p> : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow" data-testid="kerala-leads-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Location</th><th className="text-left p-3">Interest</th><th className="text-left p-3">Routed to</th><th className="text-left p-3">Status</th></tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100" data-testid="kerala-lead-row">
                      <td className="p-3 font-semibold text-slate-800">{l.name}<div className="text-xs text-slate-400 font-normal">{l.business_name}</div></td>
                      <td className="p-3 text-slate-600">{l.email}<div className="text-xs text-slate-400">{l.phone}</div></td>
                      <td className="p-3 text-slate-600">{[l.city, l.state].filter(Boolean).join(", ") || "\u2014"}</td>
                      <td className="p-3 text-slate-600">{l.package_interest || l.service_interest || l.vertical || "\u2014"}</td>
                      <td className="p-3">{l.routed_partner_name ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded px-2 py-1">{l.routed_partner_name}</span> : <span className="text-slate-300 text-xs">unassigned</span>}</td>
                      <td className="p-3">
                        <select value={l.status || "new"} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid="kerala-lead-status"
                          className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 outline-none">
                          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === "editors" && <EditorsPanel editors={editors} onChange={load} />}
        {tab === "partners" && <PartnersPanel partners={partners} onChange={load} />}
      </div>
    </div>
  );
}

const KERALA_DISTRICTS = [
  "Thiruvananthapuram", "Kollam", "Pathanamthitta", "Alappuzha", "Kottayam", "Idukki", "Ernakulam",
  "Thrissur", "Palakkad", "Malappuram", "Kozhikode", "Wayanad", "Kannur", "Kasaragod",
];

function CoverageMap({ partners }) {
  const approved = (partners || []).filter((p) => p.status === "approved");
  const covered = {};
  approved.forEach((p) => {
    const hay = `${p.district || ""} ${p.city || ""}`.toLowerCase();
    KERALA_DISTRICTS.forEach((d) => { if (hay.includes(d.toLowerCase())) covered[d] = (covered[d] || 0) + 1; });
  });
  const nCovered = Object.keys(covered).length;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow mb-4" data-testid="kerala-coverage-map">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><MapPin size={16} className="text-emerald-700" /> Kerala coverage (internal)</h3>
        <p className="text-xs text-slate-500">{nCovered}/{KERALA_DISTRICTS.length} districts covered · {approved.length} approved partner(s)</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-4">
        {KERALA_DISTRICTS.map((d) => {
          const on = !!covered[d];
          return (
            <div key={d} data-testid={`coverage-${d.toLowerCase()}`}
              className={`rounded-xl px-2.5 py-3 text-center border ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-400 border-dashed border-slate-300"}`}>
              <p className="text-[11px] font-semibold leading-tight">{d}</p>
              <p className="text-[10px] mt-1 opacity-80">{on ? `${covered[d]} partner${covered[d] > 1 ? "s" : ""}` : "Open"}</p>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Green = has an approved partner · Dashed = open territory. Kept internal for now (not shown publicly).</p>
    </div>
  );
}

function PartnersPanel({ partners, onChange }) {
  const setStatus = async (id, status) => {
    try {
      const res = await adminPartnerStatus(TID, id, { status });
      if (res?.initial_password) {
        window.prompt(`Partner login created. Share these credentials:\nEmail: ${res.email}\nPassword (copy now):`, res.initial_password);
      }
      toast.success(`Partner ${status}`); onChange();
    } catch { toast.error("Update failed"); }
  };
  const del = async (id, name) => {
    if (!window.confirm(`Delete application from ${name}?`)) return;
    try { await adminPartnerDelete(TID, id); toast.success("Removed"); onChange(); }
    catch { toast.error("Delete failed"); }
  };
  if (partners === null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-emerald-700" size={24} /></div>;
  if (!partners.length) return <p className="text-slate-500 text-center py-16">No partner applications yet. Share <span className="font-mono">/kerala/partners</span> to recruit local partners.</p>;
  const badge = { approved: "text-green-700 bg-green-50", rejected: "text-rose-700 bg-rose-50", pending: "text-amber-700 bg-amber-50" };
  return (
    <div className="space-y-3" data-testid="kerala-partners-list">
      <CoverageMap partners={partners} />
      {partners.map((p) => (
        <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 card-shadow flex flex-wrap items-center gap-3" data-testid="kerala-partner-row">
          <div className="flex-1 min-w-[200px]">
            <p className="font-semibold text-slate-800">{p.name} <span className="text-slate-400 font-normal text-sm">· {p.business_name || "—"}</span></p>
            <p className="text-xs text-slate-500 mt-0.5"><MapPin size={11} className="inline -mt-0.5" /> {[p.city, p.district, p.state].filter(Boolean).join(", ")}</p>
            <p className="text-xs text-slate-400 mt-0.5">{p.email} · {p.phone || "no phone"} · {p.experience || "—"}</p>
          </div>
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${badge[p.status] || badge.pending}`}>{p.status}</span>
          <div className="flex gap-2">
            <button onClick={() => setStatus(p.id, "approved")} disabled={p.status === "approved"} data-testid="partner-approve" className="inline-flex items-center gap-1 text-sm font-semibold text-green-700 bg-green-50 rounded-lg px-3 py-2 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed"><Check size={14} /> Approve</button>
            <button onClick={() => setStatus(p.id, "rejected")} disabled={p.status === "rejected"} data-testid="partner-reject" className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600 bg-rose-50 rounded-lg px-3 py-2 hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"><X size={14} /> Reject</button>
            <button onClick={() => del(p.id, p.name)} data-testid="partner-delete" className="text-slate-400 p-2 hover:bg-slate-100 rounded-lg"><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EditorsPanel({ editors, onChange }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Name, valid email and an 8+ character password are required.");
      return;
    }
    setBusy(true);
    try {
      await createEditor({ ...form, tenant_id: TID });
      toast.success(`Editor ${form.email} added`);
      setForm({ name: "", email: "", password: "" });
      onChange();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Couldn't add editor"); }
    setBusy(false);
  };
  const del = async (id, email) => {
    if (!window.confirm(`Remove editor ${email}?`)) return;
    try { await deleteEditor(id); toast.success("Editor removed"); onChange(); } catch { toast.error("Delete failed"); }
  };

  const inp = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-600";
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="kerala-editors">
      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 space-y-3 h-fit" data-testid="kerala-editor-form">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><UserPlus size={17} className="text-emerald-700" /> Assign an editor by email</h3>
        <p className="text-sm text-slate-500">Editors can log in and manage Kerala.com content (CMS) only. Set an initial password to share with them.</p>
        <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className={inp} data-testid="editor-name" />
        <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="editor@email.com" type="email" className={inp} data-testid="editor-email" />
        <input value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Initial password (8+ chars)" type="text" className={inp} data-testid="editor-password" />
        <button type="submit" disabled={busy} data-testid="editor-add" className="w-full bg-emerald-700 text-white font-semibold rounded-xl px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <><UserPlus size={16} /> Add editor</>}
        </button>
      </form>
      <div>
        <h3 className="font-heading font-bold text-slate-900 mb-3">Current editors</h3>
        {editors === null ? <Loader2 className="animate-spin text-emerald-700" size={22} /> :
          editors.length === 0 ? <p className="text-slate-500 text-sm">No editors yet.</p> : (
            <div className="space-y-2" data-testid="kerala-editor-list">
              {editors.map((ed) => (
                <div key={ed.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid="kerala-editor-row">
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{ed.name}</p>
                    <p className="text-xs text-slate-400">{ed.email} · {ed.tenant_id || "kerala"}</p>
                  </div>
                  <button onClick={() => del(ed.id, ed.email)} data-testid="editor-delete" className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function PackageRow({ initial, isNew, onSaved, onCancel }) {
  const [p, setP] = useState({ ...initial, features: (initial.features || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    const payload = { ...p, price_amount: Number(p.price_amount) || 0, order: Number(p.order) || 100, features: p.features.split("\n").map((s) => s.trim()).filter(Boolean) };
    ["id", "created_at", "updated_at", "_id", "tenant_id", "currency", "currency_symbol", "slug"].forEach((k) => delete payload[k]);
    try {
      if (isNew) await adminCreateTenantPackage(TID, payload);
      else await adminUpdateTenantPackage(TID, initial.id, payload);
      toast.success(isNew ? "Package created" : "Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm(`Delete "${initial.name}"?`)) return;
    try { await adminDeleteTenantPackage(TID, initial.id); toast.success("Deleted"); onSaved(); } catch { toast.error("Delete failed"); }
  };

  const inp = "bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-600";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 card-shadow" data-testid="kerala-package-row">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input value={p.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className={inp} data-testid="kpkg-name" />
        <input value={p.category} onChange={(e) => set("category", e.target.value)} placeholder="Category" className={inp} />
        <div className="flex gap-2">
          <input type="number" value={p.price_amount} onChange={(e) => set("price_amount", e.target.value)} placeholder="₹ Amount" className={`${inp} w-full`} data-testid="kpkg-price" />
          <select value={p.price_unit} onChange={(e) => set("price_unit", e.target.value)} className={inp}>
            <option value="one-time">one-time</option><option value="month">/mo</option><option value="year">/yr</option><option value="custom">custom</option>
          </select>
        </div>
        <input type="number" value={p.order} onChange={(e) => set("order", e.target.value)} placeholder="Order" className={inp} />
      </div>
      <input value={p.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Tagline" className={`${inp} w-full mt-3`} />
      <textarea value={p.features} onChange={(e) => set("features", e.target.value)} rows={3} placeholder="One feature per line" className={`${inp} w-full mt-3 resize-none`} />
      <div className="flex items-center gap-4 mt-3">
        <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={p.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={p.popular} onChange={(e) => set("popular", e.target.checked)} /> Popular</label>
        <div className="ml-auto flex gap-2">
          {onCancel && <button onClick={onCancel} className="text-sm text-slate-500 px-3 py-2">Cancel</button>}
          {!isNew && <button onClick={del} data-testid="kpkg-delete" className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 size={16} /></button>}
          <button onClick={save} disabled={busy} data-testid="kpkg-save" className="inline-flex items-center gap-1.5 bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
